import { createContext, useContext } from "react";
import { normalizeStoreLanguage } from "@/lib/languages";

export const ONBOARDING_UI_LANG_KEY = "project_local_onboarding_ui_language";

export const ONBOARDING_UI_LANGUAGES = [
  { code: "en", label: "EN", name: "English" },
  { code: "de", label: "DE", name: "Deutsch" },
  { code: "es", label: "ES", name: "Español" },
  { code: "pt", label: "PT", name: "Português" },
];

const en = {
  "step.business-type": "Business type",
  "step.business-details": "Business details",
  "step.plan": "Pricing",
  "step.brand": "Logo & photos",
  "step.theme": "Theme",
  "step.location": "Location & contact",
  "step.hours": "Opening hours",
  "step.products": "Products",
  "step.delivery": "Delivery",
  "step.payments": "Payments",
  "step.domain": "Domain",
  "step.legal": "Legal pages",
  "step.preview": "Preview",
  "step.agreements": "Agreements",
  "step.subscription": "Pay & go live",
  "step.publish": "Go live",

  "chrome.stepOf": "Step {current} of {total}",
  "chrome.skipToDashboard": "Skip to dashboard",
  "chrome.dashboardShort": "Dashboard",
  "chrome.now": "Now",
  "chrome.underAnHour": "Under an hour*",
  "chrome.sidebarTitle": "Build your {businessType}'s storefront from real answers.",
  "chrome.sidebarBody": "All {count} steps save into the tenant business. Subscribe to go live on the public storefront.",
  "chrome.timingDisclaimer": "*About an hour if your menu, photos, and legal documents are ready — it might take more time if you're gathering them as you go. But we will be there with you throughout.",
  "chrome.yourProgress": "Your progress",
  "chrome.langLabel": "Language",

  "actions.back": "Back",
  "actions.continue": "Continue",
  "actions.skipForNow": "Skip for now",
  "actions.goToDashboard": "Go to Dashboard",
  "actions.payAndGoLive": "Pay & go live",
  "actions.payWithStripeAndGoLive": "Pay with Stripe & go live",
  "actions.processing": "Processing…",
  "actions.continueToPayment": "Continue to payment",

  "intro.businessType.title": "What kind of business are you building?",
  "intro.businessType.body": "This config now controls terminology, default categories, prompts, and storefront CTA copy.",
  "intro.businessDetails.title": "Name and describe the business",
  "intro.businessDetails.body": "These fields update the storefront hero, SEO, and about section.",
  "intro.plan.title": "You're ready",
  "intro.plan.body": "Everything you need, one flat price. Add-ons appear later, if you want them — no commissions, ever.",
  "intro.brand.title": "Logo and photos",
  "intro.brand.body": "Upload your logo and storefront photos. Images are saved to your business and used on the public storefront.",
  "intro.theme.title": "Choose a theme",
  "intro.theme.body": "Pick a preset theme or customise colours and fonts for your storefront.",
  "intro.theme.presetBody": "Pick a preset theme. Each preview shows {name} in that palette.",
  "intro.theme.customBody": "Using your own custom colors and fonts instead of a preset.",
  "intro.location.title": "Address and contact",
  "intro.location.body": "Address, phone, and email appear on your storefront location card and footer. You can update them later in Settings.",
  "intro.hours.title": "Opening hours",
  "intro.hours.body": "Set hours per day below. Use copy to apply one day's schedule — including split lunch and dinner — to others.",
  "intro.products.title": "Products and menu",
  "intro.products.body": "Paste your menu, upload a file, or add a photo — the same import options as your owner dashboard.",
  "intro.delivery.title": "Delivery, pickup & reservations",
  "intro.delivery.body": "Configure how customers receive orders — and, if relevant, how they book a table or slot with you.",
  "intro.payments.title": "Connect payments",
  "intro.payments.body": "Connect Stripe so customers can pay by card securely. We never store card details — payouts go straight to your bank account.",
  "intro.domain.title": "Choose your store URL",
  "intro.domain.body": "You're almost ready to go live. Choose how customers will find your website — you can always switch this later from settings.",
  "intro.legal.title": "Legal & compliance documents",
  "intro.legal.body": "To host your website and follow regional laws, attach the policies customers expect. Upload PDF or Word files — you can update these later from Settings.",
  "intro.preview.title": "Storefront preview",
  "intro.preview.body": "Quick-edit hero, menu, and hours before publishing.",
  "intro.agreements.title": "Review your agreements",
  "intro.agreements.body": "Open the full Service Agreement and Data Processing Agreement, then confirm you agree before payment.",
  "intro.subscription.title": "Confirm and pay",
  "intro.subscription.body": "Review recurring monthly charges and any one-time / prepaid items. Add or remove add-ons here, then pay with Stripe to go live.",
  "intro.publish.title": "You're live!",
  "intro.publish.body": "The generated storefront now reflects onboarding answers.",

  "location.catalogLanguage": "Menu / catalog language",
  "location.catalogHint": "Choose the language your menu is written in. Translations will go from this language into others (including English).",
  "location.storefrontToggle": "Storefront language toggle",
  "location.storefrontHint": "Choose which languages customers can switch between on your storefront. The default language is always enabled.",
  "location.default": "default",
};

const de = {
  ...en,
  "step.business-type": "Geschäftstyp",
  "step.business-details": "Geschäftsdetails",
  "step.plan": "Preise",
  "step.brand": "Logo & Fotos",
  "step.theme": "Design",
  "step.location": "Standort & Kontakt",
  "step.hours": "Öffnungszeiten",
  "step.products": "Produkte",
  "step.delivery": "Lieferung",
  "step.payments": "Zahlungen",
  "step.domain": "Domain",
  "step.legal": "Rechtliches",
  "step.preview": "Vorschau",
  "step.agreements": "Vereinbarungen",
  "step.subscription": "Zahlen & live gehen",
  "step.publish": "Live gehen",

  "chrome.stepOf": "Schritt {current} von {total}",
  "chrome.skipToDashboard": "Zum Dashboard",
  "chrome.dashboardShort": "Dashboard",
  "chrome.now": "Jetzt",
  "chrome.underAnHour": "Unter einer Stunde*",
  "chrome.sidebarTitle": "Baue den Storefront deines {businessType}s aus echten Angaben.",
  "chrome.sidebarBody": "Alle {count} Schritte werden in deinem Business gespeichert. Abonniere, um live zu gehen.",
  "chrome.timingDisclaimer": "*Etwa eine Stunde, wenn Menü, Fotos und Rechtsdokumente bereit sind — länger, wenn du sie unterwegs sammelst. Wir begleiten dich dabei.",
  "chrome.yourProgress": "Dein Fortschritt",
  "chrome.langLabel": "Sprache",

  "actions.back": "Zurück",
  "actions.continue": "Weiter",
  "actions.skipForNow": "Vorerst überspringen",
  "actions.goToDashboard": "Zum Dashboard",
  "actions.payAndGoLive": "Zahlen & live gehen",
  "actions.payWithStripeAndGoLive": "Mit Stripe zahlen & live gehen",
  "actions.processing": "Wird verarbeitet…",
  "actions.continueToPayment": "Weiter zur Zahlung",

  "intro.businessType.title": "Welche Art von Geschäft baust du auf?",
  "intro.businessType.body": "Diese Einstellung steuert Terminologie, Standardkategorien, Texte und Storefront-CTA-Formulierungen.",
  "intro.businessDetails.title": "Name und Beschreibung",
  "intro.businessDetails.body": "Diese Felder aktualisieren Hero, SEO und den Über-uns-Bereich.",
  "intro.plan.title": "Du bist bereit",
  "intro.plan.body": "Alles, was du brauchst — ein fester Preis. Add-ons kommen später, wenn du sie willst. Keine Provisionen.",
  "intro.brand.title": "Logo und Fotos",
  "intro.brand.body": "Lade Logo und Fotos hoch. Sie werden gespeichert und im öffentlichen Storefront verwendet.",
  "intro.theme.title": "Design wählen",
  "intro.theme.body": "Wähle ein Preset oder passe Farben und Schriften an.",
  "intro.theme.presetBody": "Wähle ein Preset. Jede Vorschau zeigt {name} in dieser Palette.",
  "intro.theme.customBody": "Du nutzt eigene Farben und Schriften statt eines Presets.",
  "intro.location.title": "Adresse und Kontakt",
  "intro.location.body": "Adresse, Telefon und E-Mail erscheinen auf der Standortkarte und im Footer. Später in den Einstellungen änderbar.",
  "intro.hours.title": "Öffnungszeiten",
  "intro.hours.body": "Stelle Zeiten pro Tag ein. Mit Kopieren kannst du einen Tag auf andere übertragen — auch getrennte Mittags- und Abendzeiten.",
  "intro.products.title": "Produkte und Speisekarte",
  "intro.products.body": "Speisekarte einfügen, Datei hochladen oder Foto — wie im Dashboard.",
  "intro.delivery.title": "Lieferung, Abholung & Reservierungen",
  "intro.delivery.body": "Lege fest, wie Kunden Bestellungen erhalten — und falls relevant, wie sie einen Tisch oder Slot bei dir anfragen.",
  "intro.payments.title": "Zahlungen verbinden",
  "intro.payments.body": "Stripe verbinden, damit Kunden sicher per Karte zahlen. Kartendaten speichern wir nicht — Auszahlungen gehen auf dein Konto.",
  "intro.domain.title": "Store-URL wählen",
  "intro.domain.body": "Fast live. Wähle, wie Kunden deine Website finden — später jederzeit änderbar.",
  "intro.legal.title": "Rechtliche Dokumente",
  "intro.legal.body": "Lade die Richtlinien hoch, die Kunden erwarten. PDF oder Word — später in den Einstellungen aktualisierbar.",
  "intro.preview.title": "Storefront-Vorschau",
  "intro.preview.body": "Hero, Menü und Zeiten vor dem Veröffentlichen kurz bearbeiten.",
  "intro.agreements.title": "Vereinbarungen prüfen",
  "intro.agreements.body": "Service Agreement und Data Processing Agreement öffnen und bestätigen, bevor du zahlst.",
  "intro.subscription.title": "Bestätigen und zahlen",
  "intro.subscription.body": "Monatliche Kosten und Einmalposten prüfen. Add-ons hier anpassen, dann mit Stripe zahlen und live gehen.",
  "intro.publish.title": "Du bist live!",
  "intro.publish.body": "Der Storefront spiegelt jetzt deine Onboarding-Angaben wider.",

  "location.catalogLanguage": "Menü- / Katalogsprache",
  "location.catalogHint": "Sprache, in der dein Menü geschrieben ist. Übersetzungen starten von hier.",
  "location.storefrontToggle": "Storefront-Sprachauswahl",
  "location.storefrontHint": "Sprachen, zwischen denen Kunden im Storefront wechseln können. Die Standardsprache ist immer aktiv.",
  "location.default": "Standard",
};

const es = {
  ...en,
  "step.business-type": "Tipo de negocio",
  "step.business-details": "Detalles del negocio",
  "step.plan": "Precios",
  "step.brand": "Logo y fotos",
  "step.theme": "Tema",
  "step.location": "Ubicación y contacto",
  "step.hours": "Horario",
  "step.products": "Productos",
  "step.delivery": "Entrega",
  "step.payments": "Pagos",
  "step.domain": "Dominio",
  "step.legal": "Legal",
  "step.preview": "Vista previa",
  "step.agreements": "Acuerdos",
  "step.subscription": "Pagar y publicar",
  "step.publish": "Publicar",

  "chrome.stepOf": "Paso {current} de {total}",
  "chrome.skipToDashboard": "Ir al panel",
  "chrome.dashboardShort": "Panel",
  "chrome.now": "Ahora",
  "chrome.underAnHour": "Menos de una hora*",
  "chrome.sidebarTitle": "Crea la tienda de tu {businessType} con respuestas reales.",
  "chrome.sidebarBody": "Los {count} pasos se guardan en tu negocio. Suscríbete para publicar.",
  "chrome.timingDisclaimer": "*Unas hora si menú, fotos y documentos están listos — más si los reúnes sobre la marcha. Te acompañamos.",
  "chrome.yourProgress": "Tu progreso",
  "chrome.langLabel": "Idioma",

  "actions.back": "Atrás",
  "actions.continue": "Continuar",
  "actions.skipForNow": "Omitir por ahora",
  "actions.goToDashboard": "Ir al panel",
  "actions.payAndGoLive": "Pagar y publicar",
  "actions.payWithStripeAndGoLive": "Pagar con Stripe y publicar",
  "actions.processing": "Procesando…",
  "actions.continueToPayment": "Continuar al pago",

  "intro.businessType.title": "¿Qué tipo de negocio estás creando?",
  "intro.businessType.body": "Esta configuración controla terminología, categorías, textos y el CTA del escaparate.",
  "intro.businessDetails.title": "Nombre y descripción",
  "intro.businessDetails.body": "Estos campos actualizan el hero, el SEO y la sección sobre nosotros.",
  "intro.plan.title": "Listo para empezar",
  "intro.plan.body": "Todo lo necesario, un precio fijo. Los extras aparecen después si los quieres — sin comisiones.",
  "intro.brand.title": "Logo y fotos",
  "intro.brand.body": "Sube logo y fotos. Se guardan y se usan en la tienda pública.",
  "intro.theme.title": "Elige un tema",
  "intro.theme.body": "Elige un preset o personaliza colores y tipografías.",
  "intro.theme.presetBody": "Elige un preset. Cada vista previa muestra {name} en esa paleta.",
  "intro.theme.customBody": "Estás usando tus propios colores y tipografías en lugar de un preset.",
  "intro.location.title": "Dirección y contacto",
  "intro.location.body": "Dirección, teléfono y email aparecen en la ficha de ubicación y el pie. Puedes cambiarlos después en Ajustes.",
  "intro.hours.title": "Horario de apertura",
  "intro.hours.body": "Define horario por día. Copia un día a otros, incluidos turnos de comida y cena.",
  "intro.products.title": "Productos y menú",
  "intro.products.body": "Pega el menú, sube un archivo o una foto — igual que en el panel.",
  "intro.delivery.title": "Entrega, recogida y reservas",
  "intro.delivery.body": "Configura cómo reciben los pedidos los clientes — y, si aplica, cómo reservan mesa o franja contigo.",
  "intro.payments.title": "Conectar pagos",
  "intro.payments.body": "Conecta Stripe para cobrar con tarjeta. No guardamos datos de tarjeta — los cobros van a tu cuenta.",
  "intro.domain.title": "Elige la URL de tu tienda",
  "intro.domain.body": "Casi listo. Elige cómo te encuentran los clientes — puedes cambiarlo después.",
  "intro.legal.title": "Documentos legales",
  "intro.legal.body": "Adjunta las políticas que esperan tus clientes. PDF o Word — actualizables después en Ajustes.",
  "intro.preview.title": "Vista previa de la tienda",
  "intro.preview.body": "Edita rápido hero, menú y horario antes de publicar.",
  "intro.agreements.title": "Revisa tus acuerdos",
  "intro.agreements.body": "Abre el Service Agreement y el Data Processing Agreement y confirma antes de pagar.",
  "intro.subscription.title": "Confirmar y pagar",
  "intro.subscription.body": "Revisa cargos mensuales y únicos. Ajusta extras aquí, paga con Stripe y publica.",
  "intro.publish.title": "¡Ya estás en vivo!",
  "intro.publish.body": "La tienda ya refleja tus respuestas del onboarding.",

  "location.catalogLanguage": "Idioma del menú / catálogo",
  "location.catalogHint": "Idioma en el que está escrito el menú. Las traducciones parten de aquí.",
  "location.storefrontToggle": "Idiomas de la tienda",
  "location.storefrontHint": "Idiomas entre los que pueden cambiar los clientes. El idioma por defecto siempre está activo.",
  "location.default": "por defecto",
};

const pt = {
  ...en,
  "step.business-type": "Tipo de negócio",
  "step.business-details": "Detalhes do negócio",
  "step.plan": "Preços",
  "step.brand": "Logo e fotos",
  "step.theme": "Tema",
  "step.location": "Localização e contacto",
  "step.hours": "Horário",
  "step.products": "Produtos",
  "step.delivery": "Entrega",
  "step.payments": "Pagamentos",
  "step.domain": "Domínio",
  "step.legal": "Legal",
  "step.preview": "Pré-visualização",
  "step.agreements": "Acordos",
  "step.subscription": "Pagar e publicar",
  "step.publish": "Publicar",

  "chrome.stepOf": "Passo {current} de {total}",
  "chrome.skipToDashboard": "Ir para o painel",
  "chrome.dashboardShort": "Painel",
  "chrome.now": "Agora",
  "chrome.underAnHour": "Menos de uma hora*",
  "chrome.sidebarTitle": "Cria a loja do teu {businessType} com respostas reais.",
  "chrome.sidebarBody": "Os {count} passos são guardados no teu negócio. Subscreve para publicar.",
  "chrome.timingDisclaimer": "*Cerca de uma hora se menu, fotos e documentos estiverem prontos — mais se fores reunindo pelo caminho. Estamos contigo.",
  "chrome.yourProgress": "O teu progresso",
  "chrome.langLabel": "Idioma",

  "actions.back": "Voltar",
  "actions.continue": "Continuar",
  "actions.skipForNow": "Saltar por agora",
  "actions.goToDashboard": "Ir para o painel",
  "actions.payAndGoLive": "Pagar e publicar",
  "actions.payWithStripeAndGoLive": "Pagar com Stripe e publicar",
  "actions.processing": "A processar…",
  "actions.continueToPayment": "Continuar para o pagamento",

  "intro.businessType.title": "Que tipo de negócio estás a criar?",
  "intro.businessType.body": "Esta configuração controla terminologia, categorias, textos e o CTA da montra.",
  "intro.businessDetails.title": "Nome e descrição",
  "intro.businessDetails.body": "Estes campos atualizam o hero, SEO e a secção sobre nós.",
  "intro.plan.title": "Estás pronto",
  "intro.plan.body": "Tudo o que precisas, um preço fixo. Extras aparecem depois se quiseres — sem comissões.",
  "intro.brand.title": "Logo e fotos",
  "intro.brand.body": "Carrega logo e fotos. São guardadas e usadas na loja pública.",
  "intro.theme.title": "Escolhe um tema",
  "intro.theme.body": "Escolhe um preset ou personaliza cores e tipografia.",
  "intro.theme.presetBody": "Escolhe um preset. Cada pré-visualização mostra {name} nessa paleta.",
  "intro.theme.customBody": "Estás a usar as tuas próprias cores e tipografia em vez de um preset.",
  "intro.location.title": "Morada e contacto",
  "intro.location.body": "Morada, telefone e email aparecem no cartão de localização e no rodapé. Podes alterar depois nas Definições.",
  "intro.hours.title": "Horário de abertura",
  "intro.hours.body": "Define horários por dia. Copia um dia para outros, incluindo almoço e jantar.",
  "intro.products.title": "Produtos e menu",
  "intro.products.body": "Cola o menu, carrega um ficheiro ou uma foto — como no painel.",
  "intro.delivery.title": "Entrega, recolha e reservas",
  "intro.delivery.body": "Define como os clientes recebem encomendas — e, se fizer sentido, como pedem mesa ou horário contigo.",
  "intro.payments.title": "Ligar pagamentos",
  "intro.payments.body": "Liga o Stripe para cobrar com cartão. Não guardamos dados do cartão — os pagamentos vão para a tua conta.",
  "intro.domain.title": "Escolhe o URL da loja",
  "intro.domain.body": "Quase pronto. Escolhe como os clientes encontram o site — podes mudar depois.",
  "intro.legal.title": "Documentos legais",
  "intro.legal.body": "Anexa as políticas que os clientes esperam. PDF ou Word — atualizáveis depois nas Definições.",
  "intro.preview.title": "Pré-visualização da loja",
  "intro.preview.body": "Edita rapidamente hero, menu e horário antes de publicar.",
  "intro.agreements.title": "Rever os teus acordos",
  "intro.agreements.body": "Abre o Service Agreement e o Data Processing Agreement e confirma antes de pagar.",
  "intro.subscription.title": "Confirmar e pagar",
  "intro.subscription.body": "Revisa cobranças mensais e únicas. Ajusta extras aqui, paga com Stripe e publica.",
  "intro.publish.title": "Estás online!",
  "intro.publish.body": "A loja já reflete as tuas respostas do onboarding.",

  "location.catalogLanguage": "Idioma do menu / catálogo",
  "location.catalogHint": "Idioma em que o menu está escrito. As traduções partem daqui.",
  "location.storefrontToggle": "Idiomas da loja",
  "location.storefrontHint": "Idiomas entre os quais os clientes podem mudar. O idioma predefinido está sempre ativo.",
  "location.default": "predefinido",
};

const dictionaries = { en, de, es, pt };

export function readStoredOnboardingUiLanguage() {
  try {
    return normalizeStoreLanguage(window.localStorage.getItem(ONBOARDING_UI_LANG_KEY) || "en");
  } catch {
    return "en";
  }
}

export function storeOnboardingUiLanguage(code) {
  const next = normalizeStoreLanguage(code);
  try {
    window.localStorage.setItem(ONBOARDING_UI_LANG_KEY, next);
  } catch {
    // ignore
  }
  return next;
}

export function getOnboardingT(lang = "en") {
  const code = normalizeStoreLanguage(lang);
  const dict = dictionaries[code] || dictionaries.en;
  return (key, vars = {}) => {
    let text = dict[key] ?? dictionaries.en[key] ?? key;
    for (const [name, value] of Object.entries(vars)) {
      text = String(text).split(`{${name}}`).join(String(value ?? ""));
    }
    return text;
  };
}

export const OnboardingI18nContext = createContext({
  lang: "en",
  setLang: () => {},
  t: getOnboardingT("en"),
});

export function useOnboardingI18n() {
  return useContext(OnboardingI18nContext);
}
